import debounce from "lodash-es/debounce";
import { useCallback, useEffect, useRef, useState } from "react";

import { useLocalStorage } from "../../../common/hooks/useLocalStorage";
import { Nullable } from "../../../common/types/types";
import { ICategoryList } from "../types";

const PRODUCT_LIST_SCROLL_KEY = "PRODUCT_LIST_SCROLL";

export const useListPageHandler = (
	categories: ICategoryList[],
	categoryListHeightRef?: React.MutableRefObject<number>
) => {
	const { storedValue, setValue } = useLocalStorage<{
		scrollTop: string;
		activeCategoryId: Nullable<string>;
	}>(PRODUCT_LIST_SCROLL_KEY, {
		scrollTop: "0",
		activeCategoryId: null,
	});
	const [activeCategoryId, setActiveCategoryId] = useState<string | null>(() => {
		if (storedValue.activeCategoryId) {
			return storedValue.activeCategoryId;
		} else if (categories?.length > 0) {
			return categories[0]?.id;
		} else {
			return null;
		}
	});

	const isClickScrolling = useRef(false);
	const clickScrollTimeoutRef = useRef<number | null>();
	const scrollIdleHandlerRef = useRef<(() => void) | null>(null);
	const productListRef = useRef<HTMLDivElement>(null);
	const productSectionRefs = useRef<Map<string, HTMLDivElement | null>>(new Map());
	const [bannerAnimateClass, setBannerAnimateClass] = useState("none");

	const handleScroll = useCallback(
		debounce(() => {
			if (productListRef.current && !isClickScrolling.current) {
				setValue({
					scrollTop: productListRef.current.scrollTop.toString(),
					activeCategoryId: activeCategoryId?.toString() ?? null,
				});
			}
		}, 200),
		[activeCategoryId, setValue]
	);

	useEffect(() => {
		const savedPosition = storedValue.scrollTop;

		setTimeout(() => {
			setBannerAnimateClass("ShowAnimate");
		}, 100);

		if (productListRef.current && savedPosition) {
			productListRef.current.scrollTo({
				top: Number.parseInt(savedPosition, 10),
				behavior: "instant",
			});
		}

		const currentContainer = productListRef.current;

		currentContainer?.addEventListener("scroll", handleScroll, { passive: true });

		return () => {
			currentContainer?.removeEventListener("scroll", handleScroll);
		};
	}, []);

	const handleCategoryClick = useCallback(
		(categoryId: string) => {
			setActiveCategoryId(categoryId);
			isClickScrolling.current = true;

			const isFirstCategory = categories.length > 0 && categoryId === categories[0]?.id;
			const targetScrollTop = isFirstCategory ? 0 : calculateTargetScrollTop(categoryId);

			if (targetScrollTop !== null) {
				scrollToPosition(targetScrollTop);
				resetClickScrolling();
			} else {
				isClickScrolling.current = false;
			}
		},
		[categories]
	);

	const calculateTargetScrollTop = (categoryId: string): number | null => {
		const targetElement = productSectionRefs.current.get(categoryId);

		if (!targetElement || !productListRef.current) return null;

		const containerTop = productListRef.current.getBoundingClientRect().top;
		const targetTop = targetElement.getBoundingClientRect().top;
		const scrollTop = productListRef.current.scrollTop;

		return scrollTop + (targetTop - containerTop);
	};

	const scrollToPosition = (targetScrollTop: number) => {
		const container = productListRef.current;

		if (!container) return;

		const offset = categoryListHeightRef?.current || 40;
		const target = Math.max(targetScrollTop - offset, 0);
		const distance = Math.abs(container.scrollTop - target);

		// Defer scrollTo to the next frame so React has committed the
		// activeCategoryId state change and CategoryList's scrollIntoView
		// (triggered by that commit) has run first. Without this, the sibling
		// scrollIntoView call traverses scrollable ancestors and cancels our
		// vertical smooth scroll, leaving the list at the original position.
		requestAnimationFrame(() => {
			container.scrollTo({
				top: target,
				behavior: distance > 2000 ? "instant" : "smooth",
			});
		});
	};

	const resetClickScrolling = () => {
		if (clickScrollTimeoutRef.current) clearTimeout(clickScrollTimeoutRef.current);

		const container = productListRef.current;

		if (!container) {
			isClickScrolling.current = false;

			return;
		}

		// Remove any leftover handler from a prior click before attaching a new one.
		if (scrollIdleHandlerRef.current) {
			container.removeEventListener("scroll", scrollIdleHandlerRef.current);
			scrollIdleHandlerRef.current = null;
		}

		const finish = () => {
			isClickScrolling.current = false;
			if (scrollIdleHandlerRef.current) {
				container.removeEventListener("scroll", scrollIdleHandlerRef.current);
				scrollIdleHandlerRef.current = null;
			}
			if (clickScrollTimeoutRef.current) clearTimeout(clickScrollTimeoutRef.current);
			clickScrollTimeoutRef.current = null;
		};

		const onScrollIdle = () => {
			if (clickScrollTimeoutRef.current) clearTimeout(clickScrollTimeoutRef.current);
			clickScrollTimeoutRef.current = globalThis.setTimeout(finish, 200);
		};

		scrollIdleHandlerRef.current = onScrollIdle;
		container.addEventListener("scroll", onScrollIdle, { passive: true });

		// Initial schedule covers the case where scrollTo lands without firing scroll
		// (target already in view) so finish still runs.
		clickScrollTimeoutRef.current = globalThis.setTimeout(finish, 200);
	};

	const handleScrollUpdateCategory = useCallback(
		(categoryId: string) => {
			if (!isClickScrolling.current) {
				setActiveCategoryId((prevId) => {
					if (prevId !== categoryId) {
						// Update localStorage immediately when category changes
						setValue({
							scrollTop: productListRef.current?.scrollTop.toString() ?? "0",
							activeCategoryId: categoryId,
						});

						return categoryId;
					}

					return prevId;
				});
			}
		},
		[setValue]
	);

	return {
		productListRef,
		productSectionRefs,
		bannerAnimateClass,
		handleScrollUpdateCategory,
		handleCategoryClick,
		handleScroll,
		isClickScrolling,
		activeCategoryId,
	};
};
